<template>
    <div class="profile-reputation-widgets__widget-stats" :data-provider="reputation.providerName">
        <div v-for="(stat, index) in reputationStats(reputation)" :key="`${reputation.providerName}-${index}`">
            <div v-if="hasValue(stat)" :data-stat="stat.property">
                <div stat-slot>
                    <strong>{{ stat.value }}</strong>
                    <span stat-name>
                        {{ t(`profile.reputation.widgets.stat.label.${reputation.providerName}.${stat.property}` as TranslationKey) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {useTranslation} from '#imports';
    import type {ExternalReputationResource, ExternalReputationStatResource} from '~/types/api-raw-types';
    import type {TranslationKey} from '#build/i18n';

    const {t} = useTranslation();

    type Props = {
        reputation: ExternalReputationResource;
    };
    const props = defineProps<Props>();

    function reputationStats(reputation: ExternalReputationResource) {
        if (reputation.providerName === 'kaggle') {
            return reputation.stats.filter((stat) => stat.property !== 'tier');
        }

        return reputation.stats;
    }

    function hasValue(stat: ExternalReputationStatResource): boolean {
        return stat.value !== '';
    }
</script>

<style lang="scss" scoped>
    @use 'sass:color';
    @use 'sass:map';
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    /* =================================================================
        =            Stackoverflow widget variables and mixins            =
        ================================================================= */

    $gold: (
        color: #ffcc01,
        background-color: #faf4e3,
        border-color: #ece3c8,
    );
    $bronze: (
        color: #d1a684,
    );
    $silver: (
        color: #b4b8bc,
    );

    @mixin stack-overflow-medal($grade) {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--joy-core-radius-2);
        padding: 3px 10px;
        border: 1px solid transparent;

        /* search for bg color variable, if null take lighten medal color */
        @if map.get($grade, background-color) {
            background-color: map.get($grade, background-color);
        } @else {
            background-color: color.adjust(map.get($grade, color), $lightness: 27%);
        }

        /* search for border color variable if null take lighten medal color */
        @if map.get($grade, border-color) {
            border-color: map.get($grade, border-color);
        } @else {
            border-color: color.adjust(map.get($grade, color), $lightness: 22%);
        }

        [stat-name] {
            display: none;
        }

        &::before {
            content: '';
            display: block;
            width: 10px;
            height: 10px;
            margin-right: 5px;
            border-radius: var(--joy-core-radius-2);
            background-color: map.get($grade, color);
        }
    }

    /* =====  End of Stackoverflow widget variables and mixins  ====== */

    /* =================================================================
    =            Kaggle widget variables and mixins            =
    ================================================================= */

    $gold-k: (
        color: rgb(255 212 72),
        dominant-color: rgb(173 118 21),
    );
    $bronze-k: (
        color: rgb(240 186 124),
        dominant-color: rgb(142 91 61),
    );
    $silver-k: (
        color: rgb(233 233 233),
        dominant-color: rgb(131 130 128),
        border-color: #eff0f1,
    );

    @mixin kaggle-medal($grade) {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--joy-core-radius-2);
        padding: 3px 10px;
        border: 1px solid transparent;
        background-color: color.adjust(map.get($grade, color), $lightness: 27%);

        /* search for border color variable if null take lighten medal color */
        @if map.get($grade, border-color) {
            border-color: map.get($grade, border-color);
        } @else {
            border-color: color.adjust(map.get($grade, color), $lightness: 22%);
        }

        [stat-name] {
            display: none;
        }

        &::before {
            content: '';
            display: block;
            width: 15px;
            height: 15px;
            margin-right: 5px;
            border-radius: var(--joy-core-radius-3);
            background: linear-gradient(
                -45deg,
                map.get($grade, dominant-color),
                map.get($grade, dominant-color) 49%,
                map.get($grade, dominant-color) 49%,
                map.get($grade, dominant-color) 51%,
                map.get($grade, color) 51%
            );
            border-color: map.get($grade, border-color);
        }
    }

    /* =====  End of Kaggle widget variables and mixins  ====== */

    .profile-reputation-widgets__widget-stats {
        display: flex;
        flex-flow: row wrap;
        gap: var(--joy-core-spacing-2) var(--joy-core-spacing-6);
        align-items: center;
        min-height: 28px;

        [stat-name] {
            padding-left: 3px;
            text-transform: lowercase;
            font-size: var(--joy-font-size-primary-400);
        }
    }

    [data-provider='kaggle'] {
        [data-stat='bronzeBadges'] {
            @include kaggle-medal($bronze-k);
        }

        [data-stat='silverBadges'] {
            @include kaggle-medal($silver-k);
        }

        [data-stat='goldBadges'] {
            @include kaggle-medal($gold-k);
        }
    }

    [data-provider='stackoverflow'] {
        [data-stat='bronzeBadges'] {
            @include stack-overflow-medal($bronze);
        }

        [data-stat='silverBadges'] {
            @include stack-overflow-medal($silver);
        }

        [data-stat='goldBadges'] {
            @include stack-overflow-medal($gold);
        }
    }

    [data-provider='github'],
    [data-provider='behance'],
    [data-provider='twitter'] {
        & > :is(:first-child) {
            width: unset;
        }
    }
</style>
